/**
 * Utility functions for check payment verification
 * These functions encapsulate the business logic for when a check payment counts as "verified/paid"
 *
 * Business Rule: Only checks with check_status IN ('collected', 'paid') count toward paid amount
 * This matches the database function: get_sales_request_paid_amount()
 */

/**
 * Check statuses that count toward paid amount
 */
export const VALID_PAID_CHECK_STATUSES = ["collected", "paid"] as const;

export type ValidPaidCheckStatus = (typeof VALID_PAID_CHECK_STATUSES)[number];

/**
 * Determines if a check payment should count toward the paid amount
 * @param checkStatus - The current check_status value
 * @returns true if the check counts as paid (collected or paid)
 */
export function isCheckPaymentVerified(
  checkStatus: string | null | undefined
): boolean {
  return checkStatus === "collected" || checkStatus === "paid";
}

/**
 * Determines if a payment (any type) should count toward verified paid amount
 * @param paymentMethod - The payment method (check, bank_transfer, etc.)
 * @param isVerified - The is_verified field for non-check payments
 * @param checkStatus - The check_status field for check payments
 * @returns true if the payment counts as verified
 */
export function isPaymentVerified(
  paymentMethod: string | null | undefined,
  isVerified: boolean | null | undefined,
  checkStatus: string | null | undefined
): boolean {
  if (paymentMethod === "check") {
    return isCheckPaymentVerified(checkStatus);
  }
  return isVerified === true;
}

/**
 * Determines if a check status change should trigger a recalculation of paid_amount
 * This is true when the status changes between a "verified" state and a "non-verified" state
 *
 * @param oldStatus - The previous check_status value
 * @param newStatus - The new check_status value
 * @returns true if the paid_amount should be recalculated
 */
export function shouldRecalculatePaidAmount(
  oldStatus: string | null | undefined,
  newStatus: string
): boolean {
  const wasVerified = isCheckPaymentVerified(oldStatus);
  const isNowVerified = isCheckPaymentVerified(newStatus);
  return wasVerified !== isNowVerified;
}
